import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Check,
  Briefcase,
  CalendarClock,
  CircleDollarSign,
  Clock,
  Cpu,
  ExternalLink,
  FileCheck2,
  FileText,
  MessageCircle,
  Play,
  Plus,
  Receipt,
  RotateCcw,
  SlidersHorizontal,
  Sparkles,
  StickyNote,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { ArrangeableColumns, ArrangeableTiles, HiddenTray } from '@/components/dashboard-arrange';
import { EmptyState } from '@/components/empty-state';
import { EntityAvatar } from '@/components/entity-avatar';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { useMe } from '@/lib/auth';
import { useClients } from '@/lib/clients';
import { useExpiringDocuments } from '@/lib/documents';
import { useExpiringWarranties } from '@/lib/assets';
import { useOpenApprovablesCount } from '@/lib/approvables';
import {
  useDashboardCrmPulse,
  useDashboardFinancials,
  useDashboardRecentActivity,
} from '@/lib/dashboard';
import { useRunningTimer } from '@/lib/time-entries';
import { cn } from '@/lib/cn';
import { defaultColumnOf, useDashboardLayout, type CardId, type TileId } from '@/lib/dashboard-layout';
import { dollars, formatDate, formatDateTime } from '@/lib/format';
import { useSubscriptionSummary } from '@/lib/subscriptions';
import type {
  AssetData,
  DashboardActivityData,
  DocumentData,
  SubscriptionData,
} from '@/types/api';

export function Dashboard() {
  const me = useMe();
  const financials = useDashboardFinancials();
  const crmPulse = useDashboardCrmPulse();
  const activeClients = useClients({ status: 'active' });
  const expiring = useExpiringDocuments(90);
  const warranties = useExpiringWarranties(90);
  const openApprovables = useOpenApprovablesCount();
  const recent = useDashboardRecentActivity();
  const runningTimer = useRunningTimer();
  const { layout, save, reset, isCustomized } = useDashboardLayout();
  const [arranging, setArranging] = useState(false);

  const expiringSoon = (expiring.data ?? []).filter(
    (d) => d.days_until_expiry != null && d.days_until_expiry <= 30,
  );
  const warrantiesSoon = (warranties.data ?? []).filter(
    (a) => a.days_until_warranty_expiry != null && a.days_until_warranty_expiry <= 30,
  );
  const activeClientCount = activeClients.data?.meta.total ?? 0;

  const attentionTotal = expiringSoon.length + warrantiesSoon.length;

  const cards: Record<CardId, { label: string; node: ReactNode }> = {
    attention: {
      label: 'Needs attention',
      node:
        attentionTotal > 0 ? (
          <AttentionQueue expiringDocs={expiringSoon} expiringWarranties={warrantiesSoon} />
        ) : (
          <AllClearCard />
        ),
    },
    activity: {
      label: 'Recent activity',
      node: <RecentActivityCard events={recent.data ?? []} isLoading={recent.isLoading} />,
    },
    renewals: { label: 'Renewals', node: <RenewalsCard /> },
    pipeline: {
      label: 'Pipeline',
      node: <PipelinePulse pulse={crmPulse.data} isLoading={crmPulse.isLoading} />,
    },
    quick_actions: { label: 'Quick actions', node: <QuickActionsCard /> },
    expiring: {
      label: 'Expiring soon',
      node: (
        <ExpiringSummaryCard
          docs={expiring.data ?? []}
          assets={warranties.data ?? []}
          isLoading={expiring.isLoading || warranties.isLoading}
        />
      ),
    },
  };

  const tileLabel = (id: TileId) => TILE_LABELS[id];
  const cardLabel = (id: CardId) => cards[id].label;
  const renderCard = (id: CardId) => <div key={id}>{cards[id].node}</div>;

  function hideTile(id: TileId) {
    save({ ...layout, tiles: layout.tiles.filter((t) => t !== id), hiddenTiles: [...layout.hiddenTiles, id] });
  }

  function showTile(id: TileId) {
    save({ ...layout, hiddenTiles: layout.hiddenTiles.filter((t) => t !== id), tiles: [...layout.tiles, id] });
  }

  function hideCard(id: CardId) {
    save({
      ...layout,
      main: layout.main.filter((c) => c !== id),
      side: layout.side.filter((c) => c !== id),
      hiddenCards: [...layout.hiddenCards, id],
    });
  }

  /** A card comes back at the end of the column it started in. */
  function showCard(id: CardId) {
    const column = defaultColumnOf(id);
    save({ ...layout, hiddenCards: layout.hiddenCards.filter((c) => c !== id), [column]: [...layout[column], id] });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${greeting()}${me.data ? `, ${me.data.name.split(' ')[0]}` : ''}.`}
        subtitle={formatToday()}
        action={
          arranging ? (
            <div className="flex gap-2">
              {isCustomized ? (
                <Button variant="ghost" onClick={reset}>
                  <RotateCcw className="size-4" /> Reset
                </Button>
              ) : null}
              <Button onClick={() => setArranging(false)}>
                <Check className="size-4" /> Done
              </Button>
            </div>
          ) : (
            <Button variant="secondary" onClick={() => setArranging(true)}>
              <SlidersHorizontal className="size-4" /> Customize
            </Button>
          )
        }
      />

      {arranging ? (
        <p className="text-sm text-muted-foreground">
          Drag by the handle to reorder, and move cards between the two columns. The eye hides anything
          you don&apos;t need. Changes save to your account.
        </p>
      ) : null}

      {runningTimer.data ? <RunningTimerBanner /> : null}

      <KpiRow
        financials={financials.data}
        isLoading={financials.isLoading}
        activeClientCount={activeClientCount}
        openApprovables={openApprovables.data ?? 0}
        order={layout.tiles}
        arranging={arranging}
        onReorder={(tiles) => save({ ...layout, tiles })}
        onHide={hideTile}
      />

      {arranging ? (
        <ArrangeableColumns
          main={layout.main}
          side={layout.side}
          labelOf={cardLabel}
          render={(id) => cards[id].node}
          onChange={(columns) => save({ ...layout, ...columns })}
          onHide={hideCard}
        />
      ) : (
        <CardColumns main={layout.main} side={layout.side} render={renderCard} />
      )}

      {arranging ? (
        <HiddenTray
          items={[
            ...layout.hiddenTiles.map((id) => ({ key: `tile-${id}`, label: tileLabel(id), onShow: () => showTile(id) })),
            ...layout.hiddenCards.map((id) => ({ key: `card-${id}`, label: cardLabel(id), onShow: () => showCard(id) })),
          ]}
        />
      ) : null}
    </div>
  );
}

/**
 * The card columns as you normally see them. With both in use it's the wide
 * and narrow pair; with one hidden empty, the other has the full width, and
 * narrow cards sit side by side rather than stretching.
 */
function CardColumns({
  main,
  side,
  render,
}: {
  main: CardId[];
  side: CardId[];
  render: (id: CardId) => ReactNode;
}) {
  if (main.length === 0 && side.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
        Every card is hidden. Use Customize to bring some back.
      </p>
    );
  }

  if (main.length === 0) {
    return <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">{side.map(render)}</div>;
  }

  if (side.length === 0) {
    return <div className="space-y-5">{main.map(render)}</div>;
  }

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
      <div className="space-y-5 xl:col-span-2">{main.map(render)}</div>
      <div className="space-y-5">{side.map(render)}</div>
    </div>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Working late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatToday(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function RunningTimerBanner() {
  return (
    <Link to="/time">
      <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/[0.06] px-4 py-3 transition-colors hover:bg-primary/[0.1]">
        <div className="inline-flex size-9 items-center justify-center rounded-full bg-primary/15 text-primary">
          <Play className="size-4 fill-current" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium">Timer is running</div>
          <div className="text-xs text-muted-foreground">Click to manage</div>
        </div>
        <ArrowRight className="size-4 text-muted-foreground" />
      </div>
    </Link>
  );
}

const TILE_LABELS: Record<TileId, string> = {
  mrr: 'MRR',
  open_ar: 'Open AR',
  revenue: 'Revenue this month',
  hours: 'Hours this week',
  clients: 'Active clients',
};

/** Static class names, since Tailwind can't see ones built at runtime. */
const TILE_COLUMNS: Record<number, string> = {
  1: 'sm:grid-cols-1 lg:grid-cols-1',
  2: 'sm:grid-cols-2 lg:grid-cols-2',
  3: 'sm:grid-cols-3 lg:grid-cols-3',
  4: 'sm:grid-cols-3 lg:grid-cols-4',
  5: 'sm:grid-cols-3 lg:grid-cols-5',
};

function KpiRow({
  financials,
  isLoading,
  activeClientCount,
  openApprovables,
  order,
  arranging,
  onReorder,
  onHide,
}: {
  financials: ReturnType<typeof useDashboardFinancials>['data'];
  isLoading: boolean;
  activeClientCount: number;
  openApprovables: number;
  order: TileId[];
  arranging: boolean;
  onReorder: (order: TileId[]) => void;
  onHide: (id: TileId) => void;
}) {
  const tiles: Record<
    TileId,
    { value: string; sub?: string; icon: typeof Wallet; accent?: boolean; link?: string }
  > = {
    mrr: {
      value: isLoading ? '—' : dollars(financials?.mrr_cents ?? 0),
      sub: financials ? `${financials.active_subscriptions_count} active` : undefined,
      icon: TrendingUp,
      accent: true,
      link: '/subscriptions',
    },
    open_ar: {
      value: isLoading ? '—' : dollars(financials?.open_ar_cents ?? 0),
      sub: financials ? `${financials.open_invoices_count} invoices` : undefined,
      icon: Wallet,
      link: '/invoices?status=open',
    },
    revenue: {
      value: isLoading ? '—' : dollars(financials?.revenue_this_month_cents ?? 0),
      sub: 'From your ledger',
      icon: CircleDollarSign,
      link: '/accounting?tab=revenue',
    },
    hours: {
      value: isLoading ? '—' : formatHours(financials?.hours_this_week_seconds ?? 0),
      sub: financials && financials.unbilled_amount_cents > 0
        ? `${dollars(financials.unbilled_amount_cents)} unbilled`
        : undefined,
      icon: Clock,
      link: '/time',
    },
    clients: {
      value: String(activeClientCount),
      sub: `${openApprovables} open approvables`,
      icon: Users,
      link: '/clients?status=active',
    },
  };

  function body(id: TileId) {
    const t = tiles[id];
    const Icon = t.icon;
    return (
      <div
        className={`group/k h-full rounded-xl border bg-card p-4 shadow-xs transition-colors ${
          t.link ? 'hover:border-primary/40' : ''
        } ${t.accent ? 'border-primary/20' : 'border-border/70'}`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {TILE_LABELS[id]}
          </span>
          <Icon className={`size-4 ${t.accent ? 'text-primary' : 'text-muted-foreground'}`} />
        </div>
        <div className="mt-1.5 text-2xl font-semibold tabular-nums tracking-tight">{t.value}</div>
        {t.sub ? <div className="mt-0.5 text-xs text-muted-foreground">{t.sub}</div> : null}
      </div>
    );
  }

  if (order.length === 0) return null;

  const gridClass = cn('grid grid-cols-2 gap-3', TILE_COLUMNS[order.length]);

  if (arranging) {
    return (
      <ArrangeableTiles
        ids={order}
        labelOf={(id) => TILE_LABELS[id]}
        render={body}
        onReorder={onReorder}
        onHide={onHide}
        className={gridClass}
      />
    );
  }

  return (
    <div className={gridClass}>
      {order.map((id) => {
        const link = tiles[id].link;
        return link ? (
          <Link key={id} to={link} className="block h-full">
            {body(id)}
          </Link>
        ) : (
          <div key={id} className="h-full">
            {body(id)}
          </div>
        );
      })}
    </div>
  );
}

function AttentionQueue({
  expiringDocs,
  expiringWarranties,
}: {
  expiringDocs: DocumentData[];
  expiringWarranties: AssetData[];
}) {
  const total = expiringDocs.length + expiringWarranties.length;
  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-1.5 text-sm font-semibold">
            <AlertTriangle className="size-4 text-amber-600 dark:text-amber-500" />
            Needs your attention
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {total} {total === 1 ? 'item' : 'items'} waiting on you
          </p>
        </div>
      </CardHeader>
      <CardBody className="space-y-1">
        {expiringDocs.length > 0 ? (
          <Section title="Documents expiring soon" count={expiringDocs.length} icon={FileText}>
            {expiringDocs.map((d) => (
              <AttentionRow
                key={`doc-${d.id}`}
                client={d.client_name ?? `Client #${d.client_id}`}
                title={d.title}
                detail={expirationDetail(d.days_until_expiry)}
                tone={
                  d.days_until_expiry != null && d.days_until_expiry < 0 ? 'danger' : 'warning'
                }
                href={`/clients/${d.client_id}?tab=documents`}
              />
            ))}
          </Section>
        ) : null}

        {expiringWarranties.length > 0 ? (
          <Section title="Warranties expiring soon" count={expiringWarranties.length} icon={Cpu}>
            {expiringWarranties.map((a) => (
              <AttentionRow
                key={`asset-${a.id}`}
                client={a.client_name ?? `Client #${a.client_id}`}
                title={a.name}
                detail={expirationDetail(a.days_until_warranty_expiry)}
                tone={
                  a.days_until_warranty_expiry != null && a.days_until_warranty_expiry < 0
                    ? 'danger'
                    : 'warning'
                }
                href={`/clients/${a.client_id}?tab=assets`}
              />
            ))}
          </Section>
        ) : null}
      </CardBody>
    </Card>
  );
}

function Section({
  title,
  count,
  icon: Icon,
  children,
}: {
  title: string;
  count: number;
  icon: typeof MessageCircle;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1.5 pt-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        <Icon className="size-3.5" />
        {title}
        <span className="ml-1 rounded bg-muted px-1.5 py-0.5 tabular-nums text-muted-foreground">
          {count}
        </span>
      </div>
      <div className="divide-y divide-border/50">{children}</div>
    </div>
  );
}

function AttentionRow({
  client,
  title,
  detail,
  href,
  external = false,
  tone = 'neutral',
}: {
  client: string;
  title: string;
  detail?: string;
  href: string;
  external?: boolean;
  tone?: 'neutral' | 'warning' | 'danger';
}) {
  const dotClass =
    tone === 'danger'
      ? 'bg-destructive'
      : tone === 'warning'
        ? 'bg-amber-500'
        : 'bg-muted-foreground';

  const body = (
    <div className="group/attention flex items-start gap-3 px-2 py-2.5 transition-colors hover:bg-muted/40">
      <span className={`mt-1.5 size-1.5 shrink-0 rounded-full ${dotClass}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-sm font-medium">{title}</span>
          <span className="text-xs text-muted-foreground">· {client}</span>
        </div>
        {detail ? (
          <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{detail}</p>
        ) : null}
      </div>
      {external ? (
        <ExternalLink className="size-3.5 shrink-0 text-muted-foreground" />
      ) : (
        <ArrowRight className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/attention:opacity-100" />
      )}
    </div>
  );

  if (external) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className="block">
        {body}
      </a>
    );
  }
  return <Link to={href}>{body}</Link>;
}

function expirationDetail(days: number | null | undefined): string {
  if (days == null) return 'No expiration date';
  if (days < 0) return `Expired ${Math.abs(days)} days ago`;
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  return `Expires in ${days} days`;
}

function AllClearCard() {
  return (
    <Card>
      <CardBody>
        <EmptyState
          icon={Activity}
          title="All clear"
          description="No documents expiring soon, no warranties at risk. Good time to bill some hours or check the books."
        />
      </CardBody>
    </Card>
  );
}

function RecentActivityCard({
  events,
  isLoading,
}: {
  events: DashboardActivityData[];
  isLoading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Recent activity</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Latest events across all clients
          </p>
        </div>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <SkeletonList rows={4} />
        ) : events.length === 0 ? (
          <EmptyState
            icon={Activity}
            title="No activity yet"
            description="Log calls, send approvables, upload documents — they'll show up here."
          />
        ) : (
          <ol className="-mx-2 divide-y divide-border/50">
            {events.map((e) => (
              <ActivityRow key={e.id} event={e} />
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  );
}

function ActivityRow({ event }: { event: DashboardActivityData }) {
  const title = event.title ?? prettyType(event.type);
  return (
    <li>
      <Link
        to={`/clients/${event.client_id}?tab=timeline`}
        className="group/act flex items-start gap-3 px-2 py-2.5 transition-colors hover:bg-muted/40"
      >
        <EntityAvatar
          name={event.client_name ?? `C${event.client_id}`}
          size="sm"
          className="mt-0.5"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm font-medium">{title}</span>
            <span className="text-xs text-muted-foreground">
              · {event.client_name ?? `Client #${event.client_id}`}
            </span>
          </div>
          {event.body ? (
            <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{event.body}</p>
          ) : null}
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatDateTime(event.occurred_at)}
        </span>
      </Link>
    </li>
  );
}

/**
 * What recurring revenue needs from you next: anything at risk first, then the
 * renewals coming up. The totals live in the tiles above, so this card lists
 * names instead of repeating numbers.
 */
function RenewalsCard() {
  const { data, isLoading } = useSubscriptionSummary();

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Renewals</h3>
        <Link to="/subscriptions" className="text-xs font-medium text-primary hover:underline">
          All subscriptions
        </Link>
      </CardHeader>
      <CardBody className="space-y-4">
        {isLoading || !data ? (
          <SkeletonList rows={3} />
        ) : data.at_risk.length === 0 && data.upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing renews in the next 30 days, and nothing is at risk.</p>
        ) : (
          <>
            {data.at_risk.length > 0 ? (
              <RenewalGroup
                title="Needs attention"
                more={data.at_risk_count - data.at_risk.length}
                moreHref="/subscriptions?segment=at_risk"
                items={data.at_risk}
                warn
              />
            ) : null}
            {data.upcoming.length > 0 ? (
              <RenewalGroup
                title="Coming up"
                more={data.renewing_count - data.upcoming.length}
                moreHref="/subscriptions"
                items={data.upcoming}
              />
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}

function RenewalGroup({
  title,
  items,
  more,
  moreHref,
  warn = false,
}: {
  title: string;
  items: SubscriptionData[];
  more: number;
  moreHref: string;
  warn?: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className={`text-[11px] font-medium uppercase tracking-wider ${warn ? 'text-[var(--color-warning)]' : 'text-muted-foreground'}`}>
        {title}
      </div>
      {items.map((sub) => (
        <Link
          key={sub.id}
          to={sub.client_id ? `/clients/${sub.client_id}?tab=subscriptions` : '/subscriptions?segment=at_risk'}
          className="-mx-2 flex items-start justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-muted/40"
        >
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{sub.client_name ?? 'No client linked'}</div>
            <div className="truncate text-xs text-muted-foreground">
              {sub.display_name} · {renewalNote(sub)}
            </div>
          </div>
          <div className="shrink-0 text-right text-sm font-semibold tabular-nums">{dollars(sub.amount_cents)}</div>
        </Link>
      ))}
      {more > 0 ? (
        <Link to={moreHref} className="block text-xs font-medium text-primary hover:underline">
          {more} more
        </Link>
      ) : null}
    </div>
  );
}

function renewalNote(sub: SubscriptionData): string {
  if (sub.status === 'past_due' || sub.status === 'unpaid') return 'Payment failed';
  if (sub.status === 'paused') return 'Paused';
  if (sub.status === 'incomplete') return 'Payment not finished';
  if (sub.cancel_at_period_end && sub.current_period_end) return `Ends ${formatDate(sub.current_period_end)}`;
  return sub.current_period_end ? `Renews ${formatDate(sub.current_period_end)}` : 'Renews soon';
}

function PipelinePulse({
  pulse,
  isLoading,
}: {
  pulse: ReturnType<typeof useDashboardCrmPulse>['data'];
  isLoading: boolean;
}) {
  if (isLoading || !pulse) {
    return (
      <Card>
        <CardHeader className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Pipeline pulse</h3>
        </CardHeader>
        <CardBody>
          <SkeletonList rows={3} />
        </CardBody>
      </Card>
    );
  }

  if (
    pulse.open_leads_count === 0 &&
    pulse.new_leads_this_week === 0 &&
    pulse.total_open_tasks_count === 0
  ) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Pipeline pulse</h3>
        </CardHeader>
        <CardBody>
          <EmptyState
            icon={Sparkles}
            title="No leads yet"
            description="Capture inquiries from the website or drop one in manually."
            action={
              <Link to="/leads/new">
                <Plus className="mr-1 inline size-4 align-text-bottom" />
                New lead
              </Link>
            }
          />
        </CardBody>
      </Card>
    );
  }

  const delta = pulse.new_leads_this_week - pulse.new_leads_last_week;
  const deltaText =
    pulse.new_leads_last_week === 0
      ? pulse.new_leads_this_week > 0
        ? 'first leads of the week'
        : 'none last week either'
      : `${delta >= 0 ? '+' : ''}${delta} vs last week`;

  const rows: Array<{ label: string; value: string; sub?: string; to?: string }> = [
    {
      label: 'New this week',
      value: pulse.new_leads_this_week.toString(),
      sub: deltaText,
      to: '/leads',
    },
    {
      label: 'In pipeline',
      value: dollars(pulse.open_pipeline_cents),
      sub: `${pulse.open_leads_count} open ${pulse.open_leads_count === 1 ? 'lead' : 'leads'}`,
      to: '/leads/pipeline',
    },
    {
      label: 'Overdue tasks',
      value: pulse.overdue_tasks_count.toString(),
      sub: `of ${pulse.total_open_tasks_count} open`,
      to: '/leads/tasks',
    },
    pulse.oldest_open_lead
      ? {
          label: 'Oldest waiting',
          value: `${pulse.oldest_open_lead.days_waiting}d`,
          sub: pulse.oldest_open_lead.name,
          to: `/leads/${pulse.oldest_open_lead.id}`,
        }
      : {
          label: 'Oldest waiting',
          value: '—',
          sub: 'No open leads',
        },
  ];

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Pipeline pulse</h3>
      </CardHeader>
      <CardBody className="space-y-3">
        {rows.map((r) => {
          const inner = (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">{r.label}</div>
                {r.sub ? (
                  <div className="truncate text-xs text-muted-foreground">{r.sub}</div>
                ) : null}
              </div>
              <div
                className={`shrink-0 text-right text-sm font-semibold tabular-nums ${
                  r.label === 'Overdue tasks' && pulse.overdue_tasks_count > 0
                    ? 'text-rose-600 dark:text-rose-400'
                    : ''
                }`}
              >
                {r.value}
              </div>
            </div>
          );
          return r.to ? (
            <Link
              key={r.label}
              to={r.to}
              className="block rounded-md transition-colors hover:bg-muted/40"
            >
              {inner}
            </Link>
          ) : (
            <div key={r.label}>{inner}</div>
          );
        })}
      </CardBody>
    </Card>
  );
}

function QuickActionsCard() {
  const actions: Array<{ label: string; to: string; icon: typeof Plus }> = [
    { label: 'New client', to: '/clients/new', icon: Users },
    { label: 'New approvable', to: '/approvables', icon: FileCheck2 },
    { label: 'New project', to: '/clients?action=new-project', icon: Briefcase },
    { label: 'Log event', to: '/clients?action=log-event', icon: StickyNote },
    { label: 'Track time', to: '/time', icon: Clock },
    { label: 'Draft invoice', to: '/time?action=draft', icon: Receipt },
  ];
  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Quick actions</h3>
      </CardHeader>
      <CardBody className="grid grid-cols-2 gap-2">
        {actions.map((a) => {
          const Icon = a.icon;
          return (
            <Link key={a.label} to={a.to}>
              <div className="group/qa flex items-center gap-2 rounded-md border border-border/70 bg-card px-3 py-2 text-sm transition-colors hover:border-primary/40 hover:bg-muted/40">
                <Icon className="size-4 text-muted-foreground group-hover/qa:text-primary" />
                <span className="truncate">{a.label}</span>
              </div>
            </Link>
          );
        })}
      </CardBody>
    </Card>
  );
}

function ExpiringSummaryCard({
  docs,
  assets,
  isLoading,
}: {
  docs: DocumentData[];
  assets: AssetData[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Expiring (90 days)</h3>
        </CardHeader>
        <CardBody>
          <SkeletonList rows={2} />
        </CardBody>
      </Card>
    );
  }
  const docCount = docs.length;
  const assetCount = assets.length;
  if (docCount === 0 && assetCount === 0) return null;
  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Expiring in 90 days</h3>
      </CardHeader>
      <CardBody className="space-y-2">
        {docCount > 0 ? (
          <Link
            to="/documents?filter=expiring"
            className="group/e flex items-center gap-2 rounded-md border border-border/70 px-3 py-2 text-sm transition-colors hover:border-primary/40 hover:bg-muted/40"
          >
            <FileText className="size-4 text-muted-foreground" />
            <span className="flex-1">
              {docCount} {docCount === 1 ? 'document' : 'documents'}
            </span>
            <ArrowRight className="size-3.5 text-muted-foreground" />
          </Link>
        ) : null}
        {assetCount > 0 ? (
          <Link
            to="/assets"
            className="group/e flex items-center gap-2 rounded-md border border-border/70 px-3 py-2 text-sm transition-colors hover:border-primary/40 hover:bg-muted/40"
          >
            <Cpu className="size-4 text-muted-foreground" />
            <span className="flex-1">
              {assetCount} {assetCount === 1 ? 'warranty' : 'warranties'}
            </span>
            <ArrowRight className="size-3.5 text-muted-foreground" />
          </Link>
        ) : null}
        {docs.some((d) => d.days_until_expiry != null && d.days_until_expiry <= 30) ||
        assets.some(
          (a) => a.days_until_warranty_expiry != null && a.days_until_warranty_expiry <= 30,
        ) ? (
          <p className="flex items-center gap-1 pt-1 text-xs text-amber-600 dark:text-amber-500">
            <CalendarClock className="size-3" /> Some expire within 30 days — see attention queue
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function formatHours(seconds: number): string {
  if (seconds < 60) return '0h';
  const hours = seconds / 3600;
  if (hours < 10) return `${hours.toFixed(1)}h`;
  return `${Math.round(hours)}h`;
}

function prettyType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
}
